import { Helmet } from "react-helmet-async";
import { SITE_NAME, DEFAULT_DESCRIPTION, absoluteUrl, defaultOgImageUrl } from "@/lib/site-config";

/**
 * Meta tags, canonical, Open Graph, Twitter y JSON-LD por ruta.
 * @param {string} title — título de la página (sin sufijo de marca salvo que ya lo incluyas)
 * @param {string} [description]
 * @param {string} [path] — ruta con / inicial, ej. "/contacto"
 * @param {string} [image] — URL absoluta de imagen OG (opcional)
 * @param {boolean} [noIndex] — noindex,follow (checkout, confirmaciones, etc.)
 * @param {object|object[]} [jsonLd] — objeto o array de objetos schema.org
 * @param {"website"|"article"} [ogType]
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image,
  noIndex = false,
  jsonLd,
  ogType = "website",
}) {
  const canonical = absoluteUrl(path);
  const ogImage = image || defaultOgImageUrl();
  const pageTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  const ldNodes = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : [];

  return (
    <Helmet prioritizeSeoTags htmlAttributes={{ lang: "es" }}>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {noIndex ? (
        <meta name="robots" content="noindex, follow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="es_ES" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {ldNodes.map((node, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
}
